import { resolveRenderIdentity } from '../vehicleModels/domain/renderIdentity';

const TYRE_WIDTH = 26;
const TYRE_LENGTH = 46;
const DUAL_GAP = 3.5;

const BODY_HALF = 58;
const HUB_GAP = 8;
const SIDE_GUTTER = 30;

const HEADER_SPACE = 32;
const FOOTER_SPACE = 20;

const lengthFor = (axleCount) => 270 + 44 * Math.min(4, Math.max(0, axleCount - 2));
const MIN_AXLE_CLEARANCE = 11;
const SPACING_BIAS = 1.8;

const FITMENT_RANK = { outer: 0, single: 1, inner: 2 };

const OVERHANG = {
  generic: { front: 0.95, rear: 1 },
  car: { front: 1.24, rear: 1.24 },
  truck: { front: 0.95, rear: 1 },
  bus: { front: 0.9, rear: 0.9 },
  jcb: { front: 0.95, rear: 0.8 },
  trailer: { front: 2.2, rear: 0.9 },
  mixer: { front: 0.95, rear: 1 },
};

const BODY_SCALE = {
  generic: 1,
  car: 1, truck: 1, bus: 1.06, jcb: 0.94, trailer: 1.03, mixer: 1,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const resolveSilhouette = (configuration) => {
  const identity = resolveRenderIdentity(configuration);
  if (identity.renderModelKey) return identity.renderModelKey;

  // Layout previews from the legacy API carry an explicit server-authored
  // silhouette even before native render keys are available. Never inspect
  // Vehicle model/category/type text here.
  return configuration?.layout_template?.vehicle_class?.silhouette
    || configuration?.layout_template?.definition?.chassis
    || 'generic';
};

const axleTyreScale = (silhouette, axleIndex, axleCount) => {
  if (silhouette === 'jcb' && axleCount > 1) return axleIndex === 0 ? 0.86 : 1.16;
  return 1;
};

const groupIntoAxles = (positions) => {
  const groups = new Map();
  positions.forEach((position) => {
    const declared = position.axle_index;
    const key = declared === undefined || declared === null || declared === ''
      ? `y:${Math.round(toNumber(position.y))}`
      : `a:${declared}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(position);
  });

  return [...groups.values()]
    .map((members) => ({
      members,
      rawY: members.reduce((total, member) => total + toNumber(member.y), 0) / members.length,
      declaredIndex: toNumber(members[0].axle_index, Number.POSITIVE_INFINITY),
    }))
    .sort((a, b) => (a.declaredIndex - b.declaredIndex) || (a.rawY - b.rawY));
};

const distributeAxles = (axles, maxTyreLength, wheelbase) => {
  const minGap = maxTyreLength + MIN_AXLE_CLEARANCE;
  const rawGaps = axles.slice(1).map((axle, index) => Math.abs(axle.rawY - axles[index].rawY));
  const slack = Math.max(0, wheelbase - rawGaps.length * minGap);

  const weights = rawGaps.map((gap) => (gap > 0 ? gap ** SPACING_BIAS : 0));
  const totalWeight = weights.reduce((total, weight) => total + weight, 0);

  return rawGaps.map((gap, index) => (
    totalWeight > 0
      ? minGap + (slack * weights[index]) / totalWeight
      : minGap + slack / rawGaps.length
  ));
};

const templateHeight = (configuration) => {
  const parts = String(configuration?.layout_template?.definition?.viewBox || '')
    .trim().split(/\s+/).map(Number);
  const height = parts.length === 4 ? parts[3] : NaN;
  return Number.isFinite(height) && height > 0 ? height : null;
};

const proportions = (configuration, silhouette, axles, maxTyreLength) => {
  const drawn = lengthFor(axles.length);
  const minWheelbase = (axles.length - 1) * (maxTyreLength + MIN_AXLE_CLEARANCE);
  const height = templateHeight(configuration);
  const first = axles[0].rawY;
  const last = axles[axles.length - 1].rawY;

  if (height && last > first) {
    const scale = drawn / height;
    return {
      front: clamp(first * scale, maxTyreLength * 0.5, drawn * 0.62),
      wheelbase: Math.max((last - first) * scale, minWheelbase),
    };
  }

  const overhang = OVERHANG[silhouette] || OVERHANG.truck;
  return {
    front: maxTyreLength * overhang.front,
    wheelbase: Math.max(drawn - maxTyreLength * (overhang.front + overhang.rear), minWheelbase),
  };
};

const sideOf = (position, midX, fallbackIndex) => {
  const x = toNumber(position.x, midX);
  if (x < midX) return 'left';
  if (x > midX) return 'right';
  return fallbackIndex % 2 === 0 ? 'left' : 'right';
};

export const buildVehicleGeometry = (configuration) => {
  const positions = configuration?.positions?.length
    ? configuration.positions
    : (configuration?.layout_template?.definition?.positions || []);
  if (!positions.length) return null;

  const silhouette = resolveSilhouette(configuration);
  const bodyHalf = BODY_HALF * (BODY_SCALE[silhouette] || 1);
  const xs = positions.map((position) => toNumber(position.x));
  const midX = (Math.min(...xs) + Math.max(...xs)) / 2;

  const grouped = groupIntoAxles(positions);
  const axleCount = grouped.length;

  const scaled = grouped.map((axle, index) => {
    const scale = axleTyreScale(silhouette, index, axleCount);
    return { ...axle, tyreWidth: TYRE_WIDTH * scale, tyreLength: TYRE_LENGTH * scale };
  });

  const maxTyreLength = Math.max(...scaled.map((axle) => axle.tyreLength));
  const plan = proportions(configuration, silhouette, scaled, maxTyreLength);
  const gaps = axleCount > 1 ? distributeAxles(scaled, maxTyreLength, plan.wheelbase) : [];

  let cursor = HEADER_SPACE + plan.front;
  const axles = scaled.map((axle, index) => {
    if (index > 0) cursor += gaps[index - 1];
    const y = cursor;

    const bySide = { left: [], right: [] };
    axle.members.forEach((position, memberIndex) => {
      bySide[sideOf(position, midX, memberIndex)].push(position);
    });

    const innerCentre = bodyHalf + HUB_GAP + axle.tyreWidth / 2;

    const tyres = Object.entries(bySide).flatMap(([side, members]) => {
      const ordered = [...members].sort((a, b) => {
        const distance = Math.abs(toNumber(b.x, midX) - midX) - Math.abs(toNumber(a.x, midX) - midX);
        if (Math.abs(distance) > 0.001) return distance;
        return (FITMENT_RANK[a.fitment_type] ?? 1) - (FITMENT_RANK[b.fitment_type] ?? 1);
      });
      const direction = side === 'left' ? -1 : 1;
      const last = ordered.length - 1;

      return ordered.map((position, slot) => ({
        position,
        side,
        slot,
        axleIndex: index,
        offset: direction * (innerCentre + (last - slot) * (axle.tyreWidth + DUAL_GAP)),
        y,
        width: axle.tyreWidth,
        length: axle.tyreLength,
      }));
    });

    return {
      key: `axle-${index}`,
      index,
      label: `A${index + 1}`,
      y,
      tyreLength: axle.tyreLength,
      tyreWidth: axle.tyreWidth,
      dual: tyres.length > 2,
      hubReach: Math.max(...tyres.map((tyre) => Math.abs(tyre.offset))),
      tyres,
    };
  });

  const outerEdge = Math.max(...axles.flatMap((axle) => axle.tyres)
    .map((tyre) => Math.abs(tyre.offset) + tyre.width / 2));
  const centerX = Math.round(outerEdge + SIDE_GUTTER);

  const placed = axles.map((axle) => ({
    ...axle,
    tyres: axle.tyres.map((tyre) => ({ ...tyre, x: centerX + tyre.offset })),
  }));

  const frontAxleY = axles[0].y;
  const rearAxleY = axles[axles.length - 1].y;

  const body = {
    frontAxleY,
    rearAxleY,
    topEdge: HEADER_SPACE,
    bottomEdge: Math.max(HEADER_SPACE + lengthFor(axleCount), rearAxleY + maxTyreLength * 0.6),
    bodyHalf,
    outerEdge,
    maxTyreLength,
    railHalf: silhouette === 'car' ? 0 : Math.round(clamp(bodyHalf * 0.3, 14, 24)),
    beams: axles.map((axle) => ({
      y: axle.y,
      from: bodyHalf - 6,
      to: axle.hubReach,
      thickness: clamp(axle.tyreWidth * 0.24, 5, 9),
    })),
  };

  return {
    silhouette,
    centerX,
    width: centerX * 2,
    height: Math.round(body.bottomEdge + FOOTER_SPACE),
    axles: placed,
    tyres: placed.flatMap((axle) => axle.tyres),
    body,
    positionCount: positions.length,
    axleCount,
  };
};
